import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Redirect } from 'react-router-dom';

import { Search } from '@material-ui/icons';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import NotListedLocationOutlinedIcon from '@mui/icons-material/NotListedLocationOutlined';
import {
  IconButton,
  InputAdornment,
  Box,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';

import { AddLocationDrawerV2 } from './components/AddLocationDrawerV2';
import { AddLocationsSelectV2 } from './components/AddLocationsSelectV2';
import { SearchContainer, SearchFilterContainer } from './components/InventoryAnalystSearch.styles';
import { InventoryAnalystSearchV2 } from './components/InventoryAnalystSearchV2';
import { ProviderLocationsTableV2 } from './components/ProviderLocationsTableV2';
import { SkeletonGrid } from './components/SkeletonGrid';
import { DEFAULT_REGIONS_SHORT } from './helpers/regions';
import { useFetchLocationsCount } from './hooks/useFetchLocationsCount';
import { useFetchTransactions } from './hooks/useFetchTransactions';
import { useLocationsExcelDownload } from './hooks/useLocationsExcelDownload';
import { useProviderLocationsSearch } from './hooks/useProviderLocationsSearch';
import { Container, SnackBarContainer } from './styles';

import providerLocationsPlaceholder from '../../assets/images/provider_locations/providerLocations.svg';
import SnackBar from '../../components/common/Snackbar/ErrorSnackBar';
import { ButtonV2 } from '../../components/common/v2/ButtonV2/ButtonV2';
import {
  TextFieldProviderLocationsV2,
  TextFieldV2,
} from '../../components/common/v2/TextInputV2/TextInputV2';
import Layout from '../../components/Layout/ProviderLayout';
import { isV2PermissionsEnabled } from '../../helpers/featureFlags/isV2PermissionsEnabled';
import { getUserRegions } from '../../helpers/getUserRegions';
import { getSessionTeamType } from '../../helpers/roles/getTeamType';
import { isInventoryAnalyst } from '../../helpers/roles/isInventoryAnalyst';
import { getSessionUserData } from '../../helpers/session/getSessionUserData';
import useCounter from '../../hooks/Couters';
import { useProviderRbac } from '../../hooks/useProviderRbac';
import { useSyncRef } from '../../hooks/useSyncRef';
import { ProviderLocationsContext, loadState } from '../../store/ProviderLocationsContext';
import { providerLocationsAllowedRoles, useHasAnyRole } from '../../utils/permissions';

export const ProviderLocationsV2 = () => {
  const [page, setPage] = useState(1);
  const [selectedPage, setSelectedPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const [formattedaddressVal, setFormattedaddressVal] = useState('');
  const [providerName, setProviderName] = useState('');

  const [tableSearchValue, setTableSearchValue] = useState('');
  const [sortField, setSortField] = useState('provider_updated_date');
  const [sortDirection, setSortDirection] = useState('DESC');

  const [isDeleteSuccessCB, setIsDeleteSuccessCB] = useState(false);
  const [isEditDeleteSuccessCB, setIsEditDeleteSuccessCB] = useState(false);
  const [isEmptyResponse, setIsEmptyResponse] = useState(false);
  const [isEmptySearchValue, setIsEmptySearchValue] = useState(false);
  const [isHoveredFA, setIsHoveredFA] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const [issearchProviderLocationsClick, setIssearchProviderLocationsClick] = useState(false);
  const [addLocationVariant, setAddLocationVariant] = useState(null);
  const [searchFiltersResetKey, setSearchFiltersResetKey] = useState(0);

  const [finalData, setFinalData] = useState([]);
  const [majorProvidersList, setMajorProvidersList] = useState([]);
  const [responseData, setResponseData] = useState([]);

  const [searchReqTransactionsObject, setSearchReqTransactionsObject] = useState({});
  const [searchRequestObject, setSearchRequestObject] = useState({});
  const preSearchRequestObjectRef = useSyncRef(searchRequestObject);
  const preSearchRequestTransactionsObjectRef = useSyncRef(searchReqTransactionsObject);

  const userData = useMemo(() => getSessionUserData(), []);
  const teamType = useMemo(() => getSessionTeamType(), []);
  const assignedRegions = useMemo(
    () => (isV2PermissionsEnabled() ? getUserRegions() : DEFAULT_REGIONS_SHORT),
    [],
  );
  const { dispatch } = useContext(ProviderLocationsContext);

  const { providerNameRbac, isProviderInventoryManager } = useProviderRbac();

  const onRenderedChildTable = useCallback(() => setIsRendering(false), []);
  const handleMouseEnterFA = useCallback(() => setIsHoveredFA(true), []);
  const handleMouseLeaveFA = useCallback(() => setIsHoveredFA(false), []);
  const handleSelectData = useCallback((data) => setFinalData(data), []);
  const handleFormattedAddressChange = useCallback(
    (e) => setFormattedaddressVal(e.target.value),
    [],
  );

  const refreshLocations = useCallback(
    (dataStr) => {
      if (dataStr === 'isEditSuccess' || dataStr === 'isDeleteSuccess') {
        setIsEditDeleteSuccessCB(true);
        preSearchRequestTransactionsObjectRef.current = '';
      }
      if (dataStr === 'isDeleteSuccess') setIsDeleteSuccessCB(true);
    },
    [preSearchRequestTransactionsObjectRef],
  );
  const onSucesssAdd = useCallback(() => refreshLocations('isEditSuccess'), [refreshLocations]);

  const handleTableSearchChange = useCallback((searchValue) => {
    setTableSearchValue(searchValue);
  }, []);

  const handleSortChange = useCallback((field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setSelectedPage(0);
    setPage(1);
  }, []);

  const { fetchPage } = useFetchTransactions({
    rowsPerPage,
    setIsLoading,
    setIsRendering,
    setIsEmptyResponse,
    preSearchRequestTransactionsObjectRef,
    setSearchReqTransactionsObject,
    setIsEditDeleteSuccessCB,
    setResponseData,
    sortField,
    sortDirection,
  });

  const handleRefreshTransactions = useCallback(() => {
    setSortField('provider_updated_date');
    setSortDirection('DESC');
    fetchPage({
      currentPage: page,
      reqObject: searchRequestObject,
      force: true,
    });
  }, [fetchPage, page, searchRequestObject]);

  const { searchProviderLocations, fetchLocationsCount } = useProviderLocationsSearch({
    finalData,
    formattedaddressVal,
    issearchProviderLocationsClick,
    preSearchRequestTransactionsObjectRef,
    isProviderInventoryManager,
    providerName,
    providerNameRbac,
    setIsEmptySearchValue,
    setIssearchProviderLocationsClick,
    setProviderName,
    setSearchRequestObject,
    teamType,
    totalCount,
    userData,
    tableSearchValue,
    pageSize: rowsPerPage,
    isEditDeleteSuccessCB,
    preSearchRequestObjectRef,
    searchRequestObject,
    setIsEmptyResponse,
    setPage,
    setResponseData,
    setTotalCount,
    sortField,
    sortDirection,
  });

  const searchProviders = useCallback(
    (name) => {
      if (name && name.length > 1) {
        setProviderName(name);
        dispatch({ type: 'providerName', payload: name });
      } else {
        setProviderName('');
        dispatch({ type: 'providerName', payload: '' });

        const newSearchRequestObject = { ...searchRequestObject };

        if (newSearchRequestObject.providerName) {
          delete newSearchRequestObject.providerName;
        }

        if (Object.keys(newSearchRequestObject).length > 0) {
          fetchLocationsCount(newSearchRequestObject, false, rowsPerPage);
          fetchPage({ currentPage: 1, reqObject: newSearchRequestObject, force: true });
          setSearchRequestObject(newSearchRequestObject);
        } else {
          setSearchRequestObject({});
        }
      }
    },
    [
      dispatch,
      searchRequestObject,
      fetchLocationsCount,
      fetchPage,
      rowsPerPage,
      setSearchRequestObject,
    ],
  );

  const handleClearFA = useCallback(() => {
    setFormattedaddressVal('');

    const newSearchRequestObject = { ...searchRequestObject };

    if (newSearchRequestObject.providerAddress) {
      delete newSearchRequestObject.providerAddress;
    }

    if (Object.keys(newSearchRequestObject).length > 0) {
      fetchLocationsCount(newSearchRequestObject, false, rowsPerPage);
      fetchPage({ currentPage: 1, reqObject: newSearchRequestObject, force: true });
      setSearchRequestObject(newSearchRequestObject);
    } else {
      setSearchRequestObject({});
    }
  }, [formattedaddressVal, searchRequestObject, fetchLocationsCount, fetchPage, rowsPerPage]);

  const handleTableSearchClear = useCallback(() => {
    setTableSearchValue('');

    const newSearchRequestObject = { ...searchRequestObject };

    if (newSearchRequestObject.search) {
      delete newSearchRequestObject.search;
    }

    fetchLocationsCount(newSearchRequestObject, false, rowsPerPage);
    fetchPage({ currentPage: 1, reqObject: newSearchRequestObject, force: true });

    setSearchRequestObject(newSearchRequestObject);
  }, [tableSearchValue, searchRequestObject, fetchLocationsCount, fetchPage, rowsPerPage]);

  const handleGeographySearchClear = useCallback(() => {
    setFinalData([]);
    const newSearchRequestObject = { ...searchRequestObject };

    if (assignedRegions.length > 0) {
      newSearchRequestObject.region = Array.from(new Set(assignedRegions)).join('|').trim();
    } else if (newSearchRequestObject.region) {
      delete newSearchRequestObject.region;
    }

    fetchLocationsCount(newSearchRequestObject, false, rowsPerPage);
    fetchPage({ currentPage: 1, reqObject: newSearchRequestObject, force: true });

    setSearchRequestObject(newSearchRequestObject);
  }, [
    assignedRegions,
    fetchLocationsCount,
    fetchPage,
    rowsPerPage,
    searchRequestObject,
    setSearchRequestObject,
  ]);

  const handleProviderSearchClear = useCallback(() => {
    setProviderName('');
    dispatch({ type: 'providerName', payload: '' });

    const newSearchRequestObject = { ...searchRequestObject };

    if (newSearchRequestObject.providerName) {
      delete newSearchRequestObject.providerName;
    }

    fetchLocationsCount(newSearchRequestObject, false, rowsPerPage);
    fetchPage({ currentPage: 1, reqObject: newSearchRequestObject, force: true });

    setSearchRequestObject(newSearchRequestObject);
  }, [
    tableSearchValue,
    searchRequestObject,
    fetchLocationsCount,
    fetchPage,
    rowsPerPage,
    dispatch,
  ]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.target.value === '') {
        handleProviderSearchClear();
      }
      searchProviders(e.target.value, 'inputval');
    },
    [searchProviders, handleProviderSearchClear],
  );

  const { downloading, downloadLocationsData } = useLocationsExcelDownload({
    responseData,
    totalCount,
    searchRequestObject,
    providerName,
  });

  const { inventoryPortalCounter } = useCounter();

  useEffect(() => {
    inventoryPortalCounter();
    const storedState = loadState();

    setMajorProvidersList(storedState?.majorProvidersList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const justFinishedRef = useRef(false);

  useEffect(() => {
    if (isLoading) {
      justFinishedRef.current = true;
    }
  }, [isLoading]);

  useLayoutEffect(() => {
    if (!isLoading && justFinishedRef.current) {
      justFinishedRef.current = false;
      const anchor = document.getElementById('provider-locations-top');

      if (anchor) {
        anchor.scrollIntoView({ behavior: 'auto', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
    }
  }, [isLoading, selectedPage, rowsPerPage]);

  const scrollToTableTop = useCallback(() => {
    const anchor = document.getElementById('provider-locations-top');

    if (anchor) {
      anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (!searchRequestObject || Object.keys(searchRequestObject).length === 0) return;

    setPage(1);
    setSelectedPage(0);
    scrollToTableTop();

    fetchPage({ currentPage: 1, reqObject: searchRequestObject, force: false });
    fetchLocationsCount(searchRequestObject, false, rowsPerPage);
  }, [
    searchRequestObject,
    sortField,
    sortDirection,
    scrollToTableTop,
    fetchPage,
    fetchLocationsCount,
    rowsPerPage,
  ]);

  const { hasRoles } = useHasAnyRole(providerLocationsAllowedRoles);

  const userHasAccess = !isV2PermissionsEnabled() ? true : hasRoles;
  const showInventoryAnalystSearch =
    (isInventoryAnalyst() || isV2PermissionsEnabled()) && !isProviderInventoryManager;

  const handlePageChange = useCallback(
    (_e, newPage0) => {
      setSelectedPage(newPage0);
      const backendPage = newPage0 + 1;

      setPage(backendPage);
      scrollToTableTop();
      fetchPage({ currentPage: backendPage, reqObject: searchRequestObject, force: true });
    },
    [fetchPage, searchRequestObject, scrollToTableTop],
  );

  const handleRowsPerPageChange = useCallback(
    async (value) => {
      setRowsPerPage(value);
      setSelectedPage(0);
      setPage(1);

      setIsLoading(true);
      setResponseData([]);

      scrollToTableTop();

      await fetchPage({
        currentPage: 1,
        reqObject: searchRequestObject,
        force: true,
        customPageSize: value,
      });

      if (Object.keys(searchRequestObject).length > 0) {
        fetchLocationsCount(searchRequestObject, false, value);
      }
    },
    [fetchPage, searchRequestObject, scrollToTableTop, fetchLocationsCount],
  );

  const [searchHovered, setSearchHovered] = useState(false);

  const handleSearchChange = useCallback(
    (searchVal) => {
      handleTableSearchChange(searchVal);

      if (searchVal === '') {
        if (isProviderInventoryManager) {
          searchProviderLocations();
        }

        const newSearchRequestObject = { ...searchRequestObject };

        if (newSearchRequestObject.search) {
          delete newSearchRequestObject.search;
        }

        if (Object.keys(newSearchRequestObject).length > 0) {
          fetchLocationsCount(newSearchRequestObject, false, rowsPerPage);
          fetchPage({ currentPage: 1, reqObject: newSearchRequestObject, force: true });
        }
      }
    },
    [
      handleTableSearchChange,
      searchRequestObject,
      rowsPerPage,
      fetchLocationsCount,
      fetchPage,
      tableSearchValue,
    ],
  );

  const handleClearSearch = useCallback(() => {
    if (handleTableSearchClear) {
      handleTableSearchClear();
    } else if (handleTableSearchChange) {
      handleTableSearchChange('');
    }
  }, [handleTableSearchChange, handleTableSearchClear]);

  const [isEmptySearchOnStart, setIsEmptySearchOnStart] = useState(true);

  useEffect(() => {
    if (
      isProviderInventoryManager ||
      (!isEmptyResponse && !isLoading && Object.keys(searchRequestObject)?.length > 0)
    ) {
      setIsEmptySearchOnStart(false);
    }

    if (Object.keys(searchRequestObject)?.length === 0) {
      setIsEmptySearchOnStart(true);
    }
  }, [isProviderInventoryManager, isEmptyResponse, isLoading, searchRequestObject]);

  const isAnyValueSelected =
    providerName.length > 0 || formattedaddressVal.length > 0 || finalData.length > 0;

  return !userHasAccess ? (
    <Redirect to="/locations/place/-78.549668/39.014/7" />
  ) : (
    <Layout>
      <div className="pageContainer">
        <Container>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography variant="h1" fontSize="36px" fontFamily="Financier" fontWeight={600}>
              {(isInventoryAnalyst() && !isV2PermissionsEnabled()) || !isProviderInventoryManager
                ? 'Inventory Management'
                : 'Provider Locations'}
            </Typography>
            <Box display="flex" gap={2} alignItems="center">
              <ButtonV2
                color="primary"
                startIcon={<FileDownloadOutlinedIcon />}
                variant="outlined"
                onClick={downloadLocationsData}
                loadState={downloading}
                sx={{ fontFamily: 'Calibre-R' }}
              >
                Download
                {downloading ? <CircularProgress size={20} style={{ marginLeft: 8 }} /> : null}
              </ButtonV2>
              <AddLocationsSelectV2
                selectedVariant={addLocationVariant}
                setSelectedVariant={setAddLocationVariant}
              />
            </Box>
          </Box>

          <SearchFilterContainer>
            {showInventoryAnalystSearch && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexGrow: 1,
                  flexShrink: 1,
                  minWidth: 0,
                  marginRight: 2,
                  maxWidth: { xs: '100%', lg: 'calc(100% - 420px)' },
                }}
              >
                <InventoryAnalystSearchV2
                  filtersResetKey={searchFiltersResetKey}
                  finalData={finalData}
                  formattedaddressVal={formattedaddressVal}
                  handleClearFA={handleClearFA}
                  handleFormattedAddressChange={handleFormattedAddressChange}
                  handleKeyDown={handleKeyDown}
                  handleMouseEnterFA={handleMouseEnterFA}
                  handleMouseLeaveFA={handleMouseLeaveFA}
                  handleSelectData={handleSelectData}
                  handleGeographySearchClear={handleGeographySearchClear}
                  handleProviderSearchClear={handleProviderSearchClear}
                  isHoveredFA={isHoveredFA}
                  majorProvidersList={majorProvidersList}
                  searchProviderLocations={searchProviderLocations}
                  searchProviders={searchProviders}
                  isAnyValueSelected={isAnyValueSelected}
                />
              </Box>
            )}
            <Box
              display="flex"
              alignItems="center"
              sx={
                showInventoryAnalystSearch
                  ? {
                      position: { xs: 'relative', lg: 'absolute' },
                      top: { xs: 'auto', lg: '51px' },
                      right: { xs: 'auto', lg: '20px' },
                      zIndex: { xs: 'auto', lg: 1 },
                      width: { xs: '350px', lg: 'calc(100% - 250px)' },
                      maxWidth: { xs: '100%', lg: '350px' },
                      minWidth: '150px',
                      marginTop: { xs: 2, lg: 0 },
                    }
                  : {
                      flexShrink: 1,
                      minWidth: '350px',
                      maxWidth: '400px',
                    }
              }
            >
              <TextFieldProviderLocationsV2
                value={tableSearchValue || ''}
                placeholder="Search..."
                onMouseEnter={() => setSearchHovered(true)}
                onMouseLeave={() => setSearchHovered(false)}
                disabled={isEmptySearchOnStart}
                onChange={(e) => handleSearchChange(e.target.value)}
                sx={{
                  width: '200px',
                  minWidth: '150px',
                  maxWidth: '400px',
                  flexShrink: 1,
                  flexGrow: 1,
                }}
                InputProps={{
                  endAdornment:
                    searchHovered && tableSearchValue ? (
                      <InputAdornment position="end">
                        <IconButton title="Clear" onClick={handleClearSearch}>
                          <ClearRoundedIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                }}
              />
              <Button
                sx={{
                  backgroundColor: '#003F2D',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '58px',
                  height: '59px',
                  minWidth: '58px',
                  minHeight: '59px',
                  padding: 0,
                  borderRadius: 0,
                  flexShrink: 0,
                  marginLeft: '-2px',
                  '&:hover': {
                    backgroundColor: '#003F2D',
                  },
                  '&:disabled': {
                    backgroundColor: '#CAD1d3 !important',
                    cursor: 'default',
                  },
                }}
                onClick={searchProviderLocations}
                disabled={isEmptySearchOnStart}
              >
                <Search style={{ color: 'white', fontSize: '20px' }} />
              </Button>
            </Box>
          </SearchFilterContainer>

          <div id="provider-locations-top" />

          {isLoading ? (
            <SkeletonGrid />
          ) : (
            Object.keys(searchRequestObject)?.length > 0 && (
              <ProviderLocationsTableV2
                providerData={responseData}
                totalCount={totalCount}
                rowsPerPage={rowsPerPage}
                selectedPage={selectedPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                providerName={providerName}
                teamType={teamType}
                majorProvidersList={majorProvidersList}
                refreshLocations={refreshLocations}
                isRendering={isRendering}
                downloadLoading={downloading}
                onRenderedChildTable={onRenderedChildTable}
                downloadLocationsData={downloadLocationsData}
                isLoading={isLoading}
                onSort={handleSortChange}
                currentSortField={sortField}
                currentSortDirection={sortDirection}
                onSearchChange={handleTableSearchChange}
                searchValue={tableSearchValue}
                onSearchClear={handleTableSearchClear}
                onSearch={searchProviderLocations}
                isEmptyResponse={isEmptyResponse}
                onRefreshTransactions={handleRefreshTransactions}
              />
            )
          )}

          {isEmptyResponse && (
            <Box display="flex" gap={1} alignItems="center" mt={2}>
              <NotListedLocationOutlinedIcon />
              <Typography fontSize="18px" fontWeight={500}>
                No locations found
              </Typography>
            </Box>
          )}

          {isEmptySearchOnStart && !isEmptyResponse && !isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: '60%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                zIndex: 1,
              }}
            >
              <img
                src={providerLocationsPlaceholder}
                alt="Provider Locations Empty Search Placeholder"
                style={{ margin: '0 auto', display: 'block' }}
              />
              <Typography
                sx={{
                  color: '#538184',
                  fontSize: '18px',
                  fontFamily: 'Calibre-R',
                  marginTop: 2,
                }}
              >
                Apply filters to load the list of locations and enable search.
              </Typography>
            </Box>
          )}
        </Container>

        <SnackBarContainer>
          {isDeleteSuccessCB && (
            <SnackBar
              title="Location successfully deleted"
              onClose={() => setIsDeleteSuccessCB(false)}
              delay={4000}
              customClass="snackBarBlock"
            />
          )}
        </SnackBarContainer>

        <AddLocationDrawerV2
          mode={addLocationVariant}
          setMode={setAddLocationVariant}
          onSuccessAdd={onSucesssAdd}
          onRefreshTransactions={handleRefreshTransactions}
          providerNameRbac={providerNameRbac}
        />
      </div>
    </Layout>
  );
};
